import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "./api";

export const ModelSlot = {
  Embedding: "embedding",
  Chat: "chat",
  EntityExtraction: "entity_extraction",
  ChatLearning: "chat_learning",
  QueryKeywords: "query_keywords",
  ReasoningRouter: "reasoning_router",
  Rerank: "rerank",
  ImageDescription: "image_description",
  AudioTranscription: "audio_transcription",
  ChunkContext: "chunk_context",
  Dictation: "dictation",
  VoiceTraining: "voice_training",
  Utility: "utility",
  UtilityPlus: "utility_plus",
  KnowledgeRelevance: "knowledge_relevance",
  HealthAnalysis: "health_analysis",
  BackgroundResearch: "background_research",
  UserMemory: "user_memory",
  TextToSpeech: "text_to_speech",
  TaskPlanning: "task_planning",
  TaskExecution: "task_execution",
  PluginChat: "plugin_chat",
  SupportDrafting: "support_drafting",
  StoryDrafting: "story_drafting",
  ChangelogDrafting: "changelog_drafting",
  ChangelogInvestigation: "changelog_investigation",
  ChangelogReview: "changelog_review",
  ApiEvidence: "api_evidence",
  SensitiveDataReview: "sensitive_data_review",
  StoryAsBuilt: "story_as_built",
  AgentBuilder: "agent_builder",
  SpecializedAgents: "specialized_agents",
  WebTestAgents: "web_test_agents",
  FeedbackResolution: "feedback_resolution",
  BenchmarkJudge: "benchmark_judge",
  GraphRouter: "graph_router",
  SelfKnowledgeClassifier: "self_knowledge_classifier",
} as const;
export type ModelSlot = (typeof ModelSlot)[keyof typeof ModelSlot];

// Single-selection `model_settings` slots shown on `/settings/models`.
// `background_research` is intentionally excluded: it is a multi-model pool
// managed by `/settings/background-research/*`, but still appears in provider
// compatibility metadata.
export const SLOT_ORDER: readonly ModelSlot[] = [
  ModelSlot.Chat,
  ModelSlot.PluginChat,
  ModelSlot.ReasoningRouter,
  ModelSlot.GraphRouter,
  ModelSlot.SelfKnowledgeClassifier,
  ModelSlot.Embedding,
  ModelSlot.EntityExtraction,
  ModelSlot.ChunkContext,
  ModelSlot.ChatLearning,
  ModelSlot.QueryKeywords,
  ModelSlot.Rerank,
  ModelSlot.ImageDescription,
  ModelSlot.AudioTranscription,
  ModelSlot.Dictation,
  ModelSlot.VoiceTraining,
  ModelSlot.TextToSpeech,
  ModelSlot.Utility,
  ModelSlot.UtilityPlus,
  ModelSlot.KnowledgeRelevance,
  ModelSlot.HealthAnalysis,
  ModelSlot.UserMemory,
  ModelSlot.TaskPlanning,
  ModelSlot.TaskExecution,
  ModelSlot.SupportDrafting,
  ModelSlot.StoryDrafting,
  ModelSlot.ChangelogDrafting,
  ModelSlot.ChangelogInvestigation,
  ModelSlot.ChangelogReview,
  ModelSlot.ApiEvidence,
  ModelSlot.SensitiveDataReview,
  ModelSlot.StoryAsBuilt,
  ModelSlot.AgentBuilder,
  ModelSlot.SpecializedAgents,
  ModelSlot.WebTestAgents,
  ModelSlot.FeedbackResolution,
  ModelSlot.BenchmarkJudge,
];

export const SLOT_LABELS: Record<ModelSlot, string> = {
  [ModelSlot.Chat]: "Chat",
  [ModelSlot.PluginChat]: "Plugin chat (MCP)",
  [ModelSlot.Embedding]: "Embedding",
  [ModelSlot.EntityExtraction]: "Entity extraction",
  [ModelSlot.ChatLearning]: "Chat learning",
  [ModelSlot.QueryKeywords]: "Query keywords",
  [ModelSlot.ReasoningRouter]: "Reasoning router",
  [ModelSlot.Rerank]: "Rerank",
  [ModelSlot.ImageDescription]: "Image description",
  [ModelSlot.AudioTranscription]: "Audio transcription",
  [ModelSlot.ChunkContext]: "Chunk context",
  [ModelSlot.Dictation]: "Dictado en chat",
  [ModelSlot.VoiceTraining]: "Voice training",
  [ModelSlot.TextToSpeech]: "Text to speech",
  [ModelSlot.Utility]: "Utility (short)",
  [ModelSlot.UtilityPlus]: "Utility plus",
  [ModelSlot.KnowledgeRelevance]: "Relevancia de conocimiento",
  [ModelSlot.HealthAnalysis]: "Health analysis",
  [ModelSlot.BackgroundResearch]: "Investigaciones en segundo plano",
  [ModelSlot.UserMemory]: "User memory",
  [ModelSlot.TaskPlanning]: "Task planning",
  [ModelSlot.TaskExecution]: "Task execution",
  [ModelSlot.SupportDrafting]: "Support drafting (Jira)",
  [ModelSlot.StoryDrafting]: "Borradores de historias",
  [ModelSlot.ChangelogDrafting]: "Borradores del changelog",
  [ModelSlot.ChangelogInvestigation]: "Investigación previa del changelog",
  [ModelSlot.ChangelogReview]: "Revisión independiente del changelog",
  [ModelSlot.ApiEvidence]: "Expediente de evidencia de las APIs",
  [ModelSlot.SensitiveDataReview]: "Revisión de datos sensibles",
  [ModelSlot.StoryAsBuilt]: "Descripción de implementación real",
  [ModelSlot.AgentBuilder]: "Agent builder",
  [ModelSlot.SpecializedAgents]: "Agentes especializados",
  [ModelSlot.WebTestAgents]: "Agentes de testing web",
  [ModelSlot.FeedbackResolution]: "Resolución de feedback",
  [ModelSlot.BenchmarkJudge]: "Jueces de benchmark",
  [ModelSlot.GraphRouter]: "Graph router",
  [ModelSlot.SelfKnowledgeClassifier]: "Self-knowledge classifier",
};

export const ProviderName = {
  OpenAI: "openai",
  Anthropic: "anthropic",
  OpenRouter: "openrouter",
  Cohere: "cohere",
  Voyage: "voyage",
  Google: "google",
} as const;
export type ProviderName = (typeof ProviderName)[keyof typeof ProviderName];

export const PROVIDER_ORDER = [
  ProviderName.OpenAI,
  ProviderName.Anthropic,
  ProviderName.OpenRouter,
  ProviderName.Cohere,
  ProviderName.Voyage,
  ProviderName.Google,
] as const satisfies readonly ProviderName[];

export const PROVIDER_LABELS: Record<ProviderName, string> = {
  [ProviderName.OpenAI]: "OpenAI",
  [ProviderName.Anthropic]: "Anthropic",
  [ProviderName.OpenRouter]: "OpenRouter",
  [ProviderName.Cohere]: "Cohere",
  [ProviderName.Voyage]: "Voyage",
  [ProviderName.Google]: "Google",
};

// Decision engines sit IN FRONT of an LLM slot and are deliberately NOT an LLM
// provider: `ProviderName` above stays pinned to the backend
// `ModelProviderName` (see model-settings-backend-contract.test.ts). Their
// providers and models come from the backend catalog (`getDecisionEngines`),
// never from a list in the bundle; only the threshold default is a UI default.
export const DECISION_ENGINE_DEFAULT_MIN_CONFIDENCE = 0.6;

const slotSchema = z.enum(ModelSlot);
const providerSchema = z.enum(ProviderName);

// Mirrors `Capability` (backend/src/core/model_registry.py) — the resolved
// slot-capability set a model serves, exposed on the catalog responses (
// add-model-benchmarks) so a client never has to re-derive it from
// `compatible_slots`.
export const Capability = {
  Embedding: "embedding",
  Chat: "chat",
  Rerank: "rerank",
  Vision: "vision",
  Transcription: "transcription",
  RealtimeVoice: "realtime_voice",
  TextToSpeech: "text_to_speech",
} as const;
export type Capability = (typeof Capability)[keyof typeof Capability];

const capabilitySchema = z.enum(Capability);

// Precedence tier that actually supplies a slot's fallback — see
// `resolve_effective_fallback_source` in `backend/src/core/model_settings.py`.
// `db` (this row's own stored pair), `env` (legacy env-var pair, chat/
// plugin_chat/embedding only), `provider_default` (code-defined per-provider
// default, chat-capability slots only), or `none` (failover disabled for
// this slot). See `openspec/changes/generalize-llm-provider-failover`.
export const FallbackSource = {
  Db: "db",
  Env: "env",
  ProviderDefault: "provider_default",
  None: "none",
} as const;
export type FallbackSource = (typeof FallbackSource)[keyof typeof FallbackSource];

const fallbackSourceSchema = z.enum(FallbackSource);

// Non-blocking advisory returned by the backend on a successful
// model-settings write. Today the only emitter is the
// `reasoning_router_latency` rule (see
// openspec/changes/add-reasoning-router-latency-advisory/). The
// schema keeps `code` as a free-form string so future advisories
// added on the backend don't require a frontend release to surface.
export const modelSettingAdvisorySchema = z.object({
  code: z.string(),
  message: z.string(),
});
export type ModelSettingAdvisory = z.infer<typeof modelSettingAdvisorySchema>;

const pricingSchema = z
  .object({
    input_per_million_usd: z.number().nullable().optional(),
    output_per_million_usd: z.number().nullable().optional(),
    audio_input_per_million_usd: z.number().nullable().optional(),
    cached_audio_input_per_million_usd: z.number().nullable().optional(),
    audio_output_per_million_usd: z.number().nullable().optional(),
    per_million_tokens_usd: z.number().nullable().optional(),
    per_thousand_searches_usd: z.number().nullable().optional(),
  })
  .nullable();
export type ProviderPricing = z.infer<typeof pricingSchema>;

// Manual-or-legacy all-provider price limit for one text slot (USD per
// million tokens). See openspec/changes/configure-text-model-price-limits.
const slotPriceLimitSchema = z.object({
  input_per_million_usd: z.number(),
  output_per_million_usd: z.number(),
  source: z.enum(["manual", "legacy_default"]),
});

export type SlotPriceLimit = z.infer<typeof slotPriceLimitSchema>;

const slotPriceLimitResponseSchema = slotPriceLimitSchema;

const priceLimitAuditEntrySchema = z.object({
  id: z.string(),
  slot: slotSchema,
  from_input_per_million_usd: z.number().nullable(),
  from_output_per_million_usd: z.number().nullable(),
  to_input_per_million_usd: z.number().nullable(),
  to_output_per_million_usd: z.number().nullable(),
  actor_id: z.string(),
  actor_email: z.string().nullable().optional(),
  created_at: z.string(),
});
export type PriceLimitAuditEntry = z.infer<typeof priceLimitAuditEntrySchema>;
const priceLimitAuditListSchema = z.array(priceLimitAuditEntrySchema);

// Stable admission codes returned by the shared server policy.
export type AdmissionCode =
  | "selectable"
  | "incompatible_slot"
  | "model_pricing_unavailable"
  | "exceeds_input_price_limit"
  | "exceeds_output_price_limit"
  | "model_missing_structured_outputs"
  | "same_as_primary"
  | "same_as_fallback";

const admissionStatusSchema = z.object({
  codes: z.array(z.string()),
  // Server-authoritative verdict (admit-catalog-priced-models-in-selectors):
  // required, not optional — clients disable a row from this flag alone and
  // must never re-derive it from the code list, which is how the selector
  // and the final write drifted apart.
  selectable: z.boolean(),
  input_rate_per_million_usd: z.number().nullable().optional(),
  output_rate_per_million_usd: z.number().nullable().optional(),
  limit_input_per_million_usd: z.number().nullable().optional(),
  limit_output_per_million_usd: z.number().nullable().optional(),
  limit_source: z.string().nullable().optional(),
});
export type AdmissionStatus = z.infer<typeof admissionStatusSchema>;

export const modelSettingSchema = z.object({
  slot: slotSchema,
  provider: providerSchema,
  model_identifier: z.string(),
  is_default: z.boolean(),
  max_output_tokens: z.number().int().min(1).max(4096).nullish(),
  // Optional non-blocking advisories — present only on the
  // PUT response when at least one rule fired. `list` leaves this
  // out; the field is nullable on the wire (FastAPI serialises
  // `None` as `null`).
  advisories: z.array(modelSettingAdvisorySchema).nullish(),
  // Stored fallback pair for the slot (both `null` when unset) — see
  // `openspec/changes/generalize-llm-provider-failover`. `.nullish()`
  // (rather than a schema-level default) keeps the fields optional at the
  // TypeScript level so existing fixtures/tests built before this change
  // landed don't need updating.
  fallback_provider: providerSchema.nullish(),
  fallback_model: z.string().nullish(),
  // Precedence tier that would actually supply the slot's fallback right
  // now. Always present on the wire; treat an absent/undefined value the
  // same as `"none"` at call sites.
  effective_fallback_source: fallbackSourceSchema.nullish(),
  // Current pricing of the primary selection / stored fallback pair,
  // resolved server-side from the model-catalog snapshot — see
  // `openspec/changes/add-model-price-sync/specs/model_settings/spec.md`.
  // `.nullish()` + the transform below normalizes an absent field (an
  // older backend response predating this change) to `null`, same as an
  // explicit `null` (no pricing entry / no fallback configured), so call
  // sites never need to distinguish "missing" from "unknown".
  pricing: pricingSchema.nullish().transform((value) => value ?? null),
  fallback_pricing: pricingSchema.nullish().transform((value) => value ?? null),
  // Effective all-provider input/output price limit for text slots, with
  // provenance (configure-text-model-price-limits). `null` for non-text
  // slots. `.nullish()` normalizes an older backend response to `null`.
  price_limit: slotPriceLimitSchema
    .nullish()
    .transform((value) => value ?? null)
    .optional(),
  // True when the slot accepts a manual token-price limit even without a
  // legacy ceiling to display (configure-text-model-price-limits).
  price_limit_supported: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false)
    .optional(),
  // Shared-policy admission codes for the CURRENT primary binding; empty
  // array = selectable. Powers the durable invalid-binding warning.
  binding_admission_codes: z
    .array(z.string())
    .nullish()
    .transform((value) => value ?? null)
    .optional(),
  // Per-slot decision engine. The three stored values are all `null` (engine
  // off) or all set; the backend always sends them. `supported` is true only
  // where an adapter exists, and `key_configured` is the environment fact for
  // the STORED provider (the catalog carries it per provider). `.nullish()`
  // keeps every pre-existing fixture compiling and an older backend parsing;
  // call sites read a missing value as off / false (`?? null`, `?? false`). The
  // provider is a plain string on purpose: a backend that gains an engine must
  // not blank the page through an enum mismatch; the catalog names the ones the
  // page can offer.
  decision_engine_provider: z.string().nullish(),
  decision_engine_model: z.string().nullish(),
  decision_engine_min_confidence: z.number().nullish(),
  decision_engine_supported: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false)
    .optional(),
  decision_engine_key_configured: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false)
    .optional(),
});
export type ModelSetting = z.infer<typeof modelSettingSchema>;

const modelSettingsListSchema = z.array(modelSettingSchema);

export const MODEL_SETTINGS_QUERY_KEY = ["settings", "models"] as const;

export const auditEntrySchema = z.object({
  slot: slotSchema,
  from_provider: providerSchema.nullable(),
  from_model: z.string().nullable(),
  to_provider: providerSchema,
  to_model: z.string(),
  actor_id: z.string(),
  actor_email: z.string().nullable().optional(),
  created_at: z.string(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

const auditListSchema = z.array(auditEntrySchema);

export const providerModelSchema = z.object({
  provider: providerSchema,
  model_id: z.string(),
  display_name: z.string().nullable().optional(),
  pricing: pricingSchema,
  compatible_slots: z.array(slotSchema),
  // The resolved slot-capability set (add-model-benchmarks). Same shape as
  // `admission` below: `.nullish()` + the transform normalize an explicit
  // `null` to `[]`; the trailing `.optional()` keeps the field optional at
  // the TypeScript level so the many pre-existing typed fixtures across the
  // codebase that predate this field keep compiling. A key genuinely absent
  // from the payload (an older backend) parses to `undefined`, same as
  // `admission` — callers read it as `model.capabilities ?? []`.
  capabilities: z
    .array(capabilitySchema)
    .nullish()
    .transform((value) => value ?? [])
    .optional(),
  // Present only when the listing request passes a slot: the shared policy's
  // server-authoritative verdict for this row (selectors must NOT filter
  // ineligible rows out — they render them disabled).
  admission: admissionStatusSchema
    .nullish()
    .transform((value) => value ?? null)
    .optional(),
});
export type ProviderModel = z.infer<typeof providerModelSchema>;

const providerModelsResponseSchema = z.object({
  provider: providerSchema,
  provider_unavailable: z.boolean(),
  models: z.array(providerModelSchema),
});
export type ProviderModelsResponse = z.infer<typeof providerModelsResponseSchema>;

export const enabledModelSchema = z.object({
  provider: providerSchema,
  model_id: z.string(),
  display_name: z.string().nullable().optional(),
  pricing: z.object({
    input_per_million_usd: z.number().nullable(),
    output_per_million_usd: z.number().nullable(),
    audio_input_per_million_usd: z.number().nullable().optional(),
    cached_audio_input_per_million_usd: z.number().nullable().optional(),
    audio_output_per_million_usd: z.number().nullable().optional(),
    per_million_tokens_usd: z.number().nullable(),
    per_thousand_searches_usd: z.number().nullable(),
  }),
  compatible_slots: z.array(slotSchema),
  // The resolved slot-capability set (add-model-benchmarks). Same shape as
  // `providerModelSchema.admission` above: `.nullish()` + the transform
  // normalize an explicit `null` to `[]`; the trailing `.optional()` keeps
  // the field optional at the TypeScript level so the many pre-existing
  // typed fixtures across the codebase that predate this field keep
  // compiling. A key genuinely absent from the payload (an older backend)
  // parses to `undefined` — callers read it as `model.capabilities ?? []`.
  capabilities: z
    .array(capabilitySchema)
    .nullish()
    .transform((value) => value ?? [])
    .optional(),
  is_chat_default: z.boolean(),
  // Typed capability flags driving slot-aware dropdown filters and the
  // short-completion helper's per-provider parameter mapping (added by
  // add-multi-provider-short-completion-helper Gate 2). Always present
  // in the payload; the backend defaults them to false when no row
  // exists in `model_catalog_entries`.
  accepts_reasoning_effort: z.boolean(),
  accepts_minimal_effort: z.boolean(),
  accepts_extended_thinking: z.boolean(),
  accepts_structured_outputs: z.boolean(),
  // True for an OpenRouter-served hybrid-reasoning model whose upstream does
  // not honor the reasoning-disable switch (bound-hybrid-reasoning-budgets-
  // and-partial-benchmark-cost). Always present, same as the other four.
  reasoning_not_disableable: z.boolean(),
});
export type EnabledModel = z.infer<typeof enabledModelSchema>;

export const toggleEnabledModelSchema = enabledModelSchema.extend({
  pricing_warning: z.literal("model_pricing_unavailable").nullable().optional(),
});

const enabledModelsListSchema = z.array(enabledModelSchema);

/** Slice of `PUT /admin/model-catalog/profiles/{provider}/{model}/context-window` we read. */
export const modelContextWindowSchema = z.object({
  context_window_tokens: z.number().int().positive().nullable(),
});
export type ModelContextWindow = z.infer<typeof modelContextWindowSchema>;

/** Slice of one `GET /admin/model-catalog/profiles` row (admin-only; all profiles in one call). */
export const profileContextWindowSchema = z.object({
  provider: z.string(),
  model_id: z.string(),
  context_window_tokens: z.number().int().positive().nullable(),
});
export type ProfileContextWindow = z.infer<typeof profileContextWindowSchema>;

const profileContextWindowListSchema = z.array(profileContextWindowSchema);

function contextWindowPath(provider: ProviderName, modelId: string): string {
  return `/admin/model-catalog/profiles/${provider}/${encodeURIComponent(modelId)}`;
}

export const ResearchEffort = {
  Low: "low",
  Medium: "medium",
  High: "high",
} as const;
export type ResearchEffort = (typeof ResearchEffort)[keyof typeof ResearchEffort];

const researchEffortSchema = z.enum(ResearchEffort);

export const backgroundResearchModelSchema = z.object({
  provider: providerSchema,
  model_id: z.string(),
  display_name: z.string().nullable(),
  is_default: z.boolean(),
  capability_flags: z.object({
    accepts_reasoning_effort: z.boolean(),
    accepts_minimal_effort: z.boolean(),
    accepts_extended_thinking: z.boolean(),
    accepts_structured_outputs: z.boolean(),
    reasoning_not_disableable: z.boolean(),
  }),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});
export type BackgroundResearchModel = z.infer<typeof backgroundResearchModelSchema>;

const backgroundResearchModelsSchema = z.array(backgroundResearchModelSchema);

export const backgroundResearchEffortCeilingSchema = z.object({
  effort_ceiling: researchEffortSchema,
});
export type BackgroundResearchEffortCeiling = z.infer<typeof backgroundResearchEffortCeilingSchema>;

export const incompatibleModelDetailSchema = z.object({
  code: z.literal("incompatible_model"),
  slot: slotSchema,
  capability_required: z.string(),
  alternatives: z.array(z.object({ provider: providerSchema, model_identifier: z.string() })),
  // Which of the write's two pairs failed the compatibility check —
  // `"primary"` (the slot binding) or `"fallback"` (the optional fallback
  // pair). Defaulted for responses predating this field.
  field: z.enum(["primary", "fallback"]).default("primary"),
});
export type IncompatibleModelDetail = z.infer<typeof incompatibleModelDetailSchema>;

// 422 raised when the fallback pair equals the primary pair for the slot —
// either direction (setting a fallback equal to the current primary, or
// changing the primary to equal the stored fallback) trips this. See
// `FallbackEqualsPrimaryError` in `backend/src/core/model_settings.py`.
export const fallbackEqualsPrimaryDetailSchema = z.object({
  code: z.literal("fallback_equals_primary"),
  slot: slotSchema,
  message: z.string(),
});
export type FallbackEqualsPrimaryDetail = z.infer<typeof fallbackEqualsPrimaryDetailSchema>;

// 422 raised by the decision-engine registry on a settings write (object
// `detail`, unlike the list `detail` FastAPI sends for shape/range errors):
// the slot has no adapter, the provider is not registered, or the model id is
// not one the provider serves. See `DecisionEngineConfigError` in
// `backend/src/api/settings.py`.
export const decisionEngineRejectionDetailSchema = z.object({
  code: z.enum([
    "decision_engine_unsupported_slot",
    "decision_engine_unknown_provider",
    "decision_engine_unknown_model",
  ]),
  slot: slotSchema,
});
export type DecisionEngineRejectionDetail = z.infer<typeof decisionEngineRejectionDetailSchema>;

// `GET /settings/decision-engines` (admin): the registered decision-engine
// providers, their models (prices are decimal strings) and whether this
// environment holds each provider's credential. Static per deploy.
export const decisionEngineModelSchema = z.object({
  id: z.string(),
  default: z.boolean(),
  input_usd_per_million: z.string(),
  output_usd_per_million: z.string(),
});
export type DecisionEngineModel = z.infer<typeof decisionEngineModelSchema>;

export const decisionEngineProviderSchema = z.object({
  id: z.string(),
  label: z.string(),
  short_label: z.string(),
  key_configured: z.boolean(),
  models: z.array(decisionEngineModelSchema),
});
export type DecisionEngineProviderEntry = z.infer<typeof decisionEngineProviderSchema>;

export const decisionEngineCatalogSchema = z.object({
  providers: z.array(decisionEngineProviderSchema),
  supported_slots: z.array(z.string()),
});
export type DecisionEngineCatalog = z.infer<typeof decisionEngineCatalogSchema>;

export const DECISION_ENGINES_QUERY_KEY = ["settings", "decision-engines"] as const;

export const modelSettingsApi = {
  getDecisionEngines: () =>
    apiClient.get("/settings/decision-engines", decisionEngineCatalogSchema),
  list: () => apiClient.get("/settings/models", modelSettingsListSchema),
  update: (
    slot: ModelSlot,
    body: {
      provider: ProviderName;
      model_identifier: string;
      // Both present → set the fallback pair; both explicit `null` → clear
      // it; both omitted (the default, matching every pre-existing caller)
      // → leave the slot's stored fallback pair unchanged. `JSON.stringify`
      // drops keys whose value is `undefined`, so simply not setting these
      // properties is enough to omit them from the request body.
      fallback_provider?: ProviderName | null;
      fallback_model?: string | null;
      max_output_tokens?: number | null;
      // The decision-engine triple travels
      // all-or-none: all three set → enable/update; all three explicit `null`
      // → disable; all three omitted (every caller that is not the
      // decision-engine block, including swap) → the backend carries the
      // stored triple. Any partial mix is a 422.
      decision_engine_provider?: string | null;
      decision_engine_model?: string | null;
      decision_engine_min_confidence?: number | null;
    },
  ) => apiClient.put(`/settings/models/${slot}`, modelSettingSchema, body),
  audit: (
    params: {
      limit?: number;
      offset?: number;
      createdAfter?: string | null;
      createdBefore?: string | null;
    } = {},
  ) =>
    apiClient.get("/settings/models/audit", auditListSchema, {
      query: {
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
        created_after: params.createdAfter ?? undefined,
        created_before: params.createdBefore ?? undefined,
      },
    }),
  providerModels: (
    provider: ProviderName,
    options: {
      slot?: ModelSlot;
      target?: "primary" | "fallback" | "pool";
      oppositeProvider?: ProviderName;
      oppositeModel?: string;
      q?: string;
      signal?: AbortSignal;
    } = {},
  ) =>
    apiClient.get(`/providers/${provider}/models`, providerModelsResponseSchema, {
      query: {
        slot: options.slot,
        target: options.target,
        opposite_provider: options.oppositeProvider,
        opposite_model: options.oppositeModel,
        q: options.q,
      },
      ...(options.signal ? { signal: options.signal } : {}),
    }),
  upsertPriceLimit: (
    slot: ModelSlot,
    body: { input_per_million_usd: number; output_per_million_usd: number },
  ) => apiClient.put(`/settings/models/${slot}/price-limit`, slotPriceLimitResponseSchema, body),
  resetPriceLimit: (slot: ModelSlot) =>
    apiClient.delete(`/settings/models/${slot}/price-limit`, z.object({ reset: z.boolean() })),
  priceLimitAudit: (
    params: {
      limit?: number;
      offset?: number;
      createdAfter?: string | null;
      createdBefore?: string | null;
    } = {},
  ) =>
    apiClient.get("/settings/models/price-limit-audit", priceLimitAuditListSchema, {
      query: {
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
        created_after: params.createdAfter ?? undefined,
        created_before: params.createdBefore ?? undefined,
      },
    }),
  enabledModels: (options: { signal?: AbortSignal } = {}) =>
    apiClient.get(
      "/providers/models/enabled",
      enabledModelsListSchema,
      options.signal ? { signal: options.signal } : {},
    ),
  listProfileContextWindows: () =>
    apiClient.get("/admin/model-catalog/profiles", profileContextWindowListSchema),
  setContextWindow: (provider: ProviderName, modelId: string, tokens: number) =>
    apiClient.put(
      `${contextWindowPath(provider, modelId)}/context-window`,
      modelContextWindowSchema,
      {
        context_window_tokens: tokens,
      },
    ),
  setModelEnabled: (provider: ProviderName, modelId: string, enabled: boolean) =>
    apiClient.patch(
      `/providers/${provider}/models/${encodeURIComponent(modelId)}/enabled`,
      toggleEnabledModelSchema,
      { enabled },
    ),
};

/** Catalog of decision-engine providers; static per deploy, so it stays fresh for a long time. */
export function useDecisionEngineCatalog(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: DECISION_ENGINES_QUERY_KEY,
    queryFn: () => modelSettingsApi.getDecisionEngines(),
    staleTime: 60 * 60_000,
    enabled: options.enabled ?? true,
  });
}

export const backgroundResearchSettingsApi = {
  listModels: () =>
    apiClient.get("/settings/background-research/models", backgroundResearchModelsSchema),
  addModel: (provider: ProviderName, modelId: string) =>
    apiClient.post("/settings/background-research/models", backgroundResearchModelSchema, {
      provider,
      model_id: modelId,
    }),
  removeModel: (provider: ProviderName, modelId: string) =>
    apiClient.delete(
      `/settings/background-research/models/${provider}/${encodeURIComponent(modelId)}`,
      z.undefined(),
    ),
  setDefaultModel: (provider: ProviderName, modelId: string) =>
    apiClient.put(
      `/settings/background-research/models/${provider}/${encodeURIComponent(modelId)}/default`,
      backgroundResearchModelsSchema,
      {},
    ),
  getEffortCeiling: () =>
    apiClient.get(
      "/settings/background-research/effort-ceiling",
      backgroundResearchEffortCeilingSchema,
    ),
  setEffortCeiling: (effortCeiling: ResearchEffort) =>
    apiClient.put(
      "/settings/background-research/effort-ceiling",
      backgroundResearchEffortCeilingSchema,
      { effort_ceiling: effortCeiling },
    ),
};
